import React, { useState, forwardRef, useImperativeHandle, useEffect } from 'react'
import { Checkbox, Space, Row, Col, Card } from 'antd'
import Button from 'components/Button'
import Select from 'components/Select'
import Input from 'components/Form/Input'
import PlusIcon from 'components/icons/PlusIcon'
import ErrorMessage from 'utils/ErrorMessage'
import styled from 'styled-components'

const StyledCard = styled(Card)`
  margin-bottom: 10px;
  border-radius: 6px;
  border: none;
  
  .ant-card-body {
    padding: 10px;
  }
`

const StyledLabel = styled.div`
font-size: 12px;
  font-weight: 500;
  margin-bottom: 5px;
  color: #333;
`

const qualificationOptions = [
  { label: '10th', value: 'tenth' },
  { label: '12th/Diploma', value: 'twelfthOrDiploma' },
  { label: 'UG', value: 'ug' },
  { label: 'PG', value: 'pg' },
  { label: 'PG Diploma', value: 'pgDiploma' },
]

const ScoreFilter = forwardRef(({ handleScoreChange, scoreEntries, setScoreEntries }, ref) => {
  const [comparisonType, setComparisonType] = useState('greaterThan')
  const [selectedQualification, setSelectedQualification] = useState(null);
  const [percentage, setPercentage] = useState('')
 
  const [editMode, setEditMode] = useState(null)
  
  const handleComparisonChange = e => {
  setComparisonType(e.target.value);
};
  useEffect(() => {
  if (scoreEntries.length > 0) {
    setComparisonType(scoreEntries[0].comparisonType)
  }
}, [scoreEntries])
  
  const handleQualificationChange = value => {
    setSelectedQualification(value)
  }
  
  const handlePercentageChange = (e) => {
    const value = e.target.value;
  
    if (value === '') {
      setPercentage('');
      return;
    }
  
    if (!/^\d{1,3}(\.\d{0,2})?$/.test(value)) {
      ErrorMessage('Only up to 2 decimal places allowed.');
      return;
    }
  
    if (parseFloat(value) > 100) {
      ErrorMessage('Percentage cannot exceed 100.');
      return;
    }
  
    setPercentage(value);
  };
  
  const handleAdd = () => {
    if (!selectedQualification || !percentage) return
    
    // Check if qualification already exists
    const qualificationExists = scoreEntries.some(
      entry => entry.qualification.value === selectedQualification && !editMode
    )
    
    if (qualificationExists) {
      // You could add some error notification here if needed
      return
    }
    
    // Find the selected qualification option to get its label
    const selectedOption = qualificationOptions.find(option => option.value === selectedQualification)
    
    const newEntry = {
      id: Date.now(),
      qualification: {
        value: selectedQualification,
        label: selectedOption?.label || selectedQualification
      },
      comparisonType,
      percentage: parseFloat(percentage),
      nameValue: `Score - ${selectedQualification} ${comparisonType === 'greaterThan' ? '≥' : '≤'} ${percentage}%`,
      role: 'Score'
    }
    
    setScoreEntries([...scoreEntries, newEntry])
    setSelectedQualification(null)
    setPercentage('')
    
    // Call parent handler with updated entries
    if (handleScoreChange) {
      handleScoreChange([...scoreEntries, newEntry])
    }
  }
  
  const getOptionsWithDisabled = () => {
    return qualificationOptions.map(option => {
      const isSelected = scoreEntries.some(entry => 
        entry.qualification.value === option.value && 
        // Allow selecting the current qualification being edited
        (editMode ? entry.id !== editMode : true)
      );
      
      return {
        ...option,
        disabled: isSelected
      };
    });
  };

  const handleEdit = id => {
    const entryToEdit = scoreEntries.find(entry => entry.id === id)
    if (entryToEdit) {
      setEditMode(id)
      setSelectedQualification(entryToEdit.qualification.value)
      setPercentage(entryToEdit.percentage.toString())
      setComparisonType(entryToEdit.comparisonType)
    }
  }
  
  // Expose the handleAdd function to parent components
  useImperativeHandle(ref, () => ({
    handleAdd,
    handleSaveEdit
  }));
  
  const handleSaveEdit = () => {
    if (!selectedQualification || !percentage) return
    
    // Find the selected qualification option to get its label
    const selectedOption = qualificationOptions.find(option => option.value === selectedQualification)
    
    const updatedEntries = scoreEntries.map(entry => {
      if (entry.id === editMode) {
        return {
          ...entry,
          qualification: {
            value: selectedQualification,
            label: selectedOption?.label || selectedQualification
          },
          comparisonType,
          percentage: parseFloat(percentage),
          nameValue: `Score - ${selectedQualification} ${comparisonType === 'greaterThan' ? '≥' : '≤'} ${percentage}%`,
          role: 'Score'
        }
      }
      return entry
    })
    
    setScoreEntries(updatedEntries)
    setEditMode(null)
    setSelectedQualification(null)
    setPercentage('')
    
    // Call parent handler with updated entries
    if (handleScoreChange) {
      handleScoreChange(updatedEntries)
    }
  }
  
  return (
    <div>
    {/* Comparison type selection */}
    <Row style={{ marginBottom: 15, rowGap: '10px', padding: '10px' }}>
      <Col span={24}>
        <Checkbox 
          value="lessThan" 
          checked={comparisonType === 'lessThan'} 
          onChange={handleComparisonChange}
          style={{ marginRight: 10 }}
        >
          ≤ Lesser than or Equal to
        </Checkbox>
      </Col>
      <Col span={24}>
        <Checkbox 
          value="greaterThan" 
          checked={comparisonType === 'greaterThan'} 
          onChange={handleComparisonChange}
        >
          ≥ Greater than or Equal to
        </Checkbox>
      </Col>
    </Row>
    
    {/* Existing entries */}
    {scoreEntries.map(entry => (
      <StyledCard key={entry.id}>
        <Row align="middle" justify="space-between">
          <Col span={6} style={{ fontWeight: 500 }}>
            {entry.qualification.label}
          </Col>
          <Col span={5}>
            {entry.comparisonType === 'greaterThan' ? '≥' : '≤'} {entry.percentage}%
          </Col>
          <Col span={4}>
            <Button.Primary
              height="30px"
              text="Edit"
              onClick={() => handleEdit(entry.id)}
            />
          </Col>
        </Row>
      </StyledCard>
    ))}
    
    {/* Add new entry form */}
    <Row gutter={[8, 16]} style={{ marginTop: 20, rowGap: '0px' }}>
      <Col span={24}>
        <Row>
          <Col span={10}>
            <StyledLabel>Select Qualification</StyledLabel>
          </Col>
          <Col span={8}>
            <StyledLabel>Percentage</StyledLabel>
          </Col>
        </Row>
      </Col>
      <Col span={10}>
        <Select
          placeholder="Select qualification"
          style={{ width: '95%' }}
          value={selectedQualification}
          onChange={handleQualificationChange}
          options={getOptionsWithDisabled()}
        />
      </Col>
      <Col span={8}>
        
              <Input
          placeholder="e.g. 80"
          style={{ width: '100px' }}
          value={percentage}
          onChange={handlePercentageChange}
          inputMode="decimal" // Optimized for numeric input
          pattern="^\d{1,3}(\.\d{0,2})?$" // For mobile keyboards and native validation
          suffix="%"
        />
         
       
      </Col>
      <Col span={6}>
        <Button.Secondary
          text={editMode ? "Save" : "Add"}
          width="100%"
          icon={<PlusIcon color="#3249D7" />}
          dashed="true"
          onClick={editMode ? handleSaveEdit : handleAdd}
        />
      </Col>
    </Row>
  </div>
  )
})

export default ScoreFilter
